/**
 * Zero-Knowledge cryptographic primitives for the shared-vault multi-user model.
 *
 * Per-user:
 *   Master Code → Argon2id → 32-byte KEK (AES-256-GCM)
 *   KEK wraps a per-user X25519 private key. The resulting {EncryptedPrivateKey}
 *   is stored server-side. The public key is stored in the clear.
 *
 * Per-vault (one per organisation):
 *   Random 32-byte symmetric key V generated in-browser by the first admin.
 *   For every member with access, the server stores {WrappedVaultKey} which is
 *   V sealed to that member's public key via ECIES-style:
 *     ephemeralKeypair = x25519.generateKey()
 *     sharedSecret = x25519.sharedSecret(ephemeral.private, member.public)
 *     k = HKDF-SHA256(sharedSecret, info='dragonvault/wrap/v1')
 *     {iv, ciphertext} = AES-GCM(k, V)
 *
 * The server sees only ciphertext + ephemeral public keys + KDF parameters.
 * It cannot recover the Master Code, KEK, private key, or shared vault key.
 */

import { argon2id } from 'hash-wasm';
import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import type {
  EncryptedPrivateKey,
  EncryptedPayload,
  WrappedVaultKey,
} from '@dragonhq/protocol';

const GCM_IV_BYTES = 12;
const KEY_BYTES = 32;
const WRAP_INFO = new TextEncoder().encode('dragonvault/wrap/v1');

type BS = BufferSource;
const bs = (x: Uint8Array | ArrayBuffer): BS => x as unknown as BS;

function toB64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

// =====================================================================
// Argon2id KEK derivation
// =====================================================================

async function deriveKekRaw(
  masterCode: string,
  salt: Uint8Array,
  opts: { timeCost: number; memoryCost: number; parallelism: number },
): Promise<Uint8Array> {
  const hex = await argon2id({
    password: masterCode,
    salt,
    iterations: opts.timeCost,
    memorySize: opts.memoryCost,
    parallelism: opts.parallelism,
    hashLength: KEY_BYTES,
    outputType: 'hex',
  });
  const raw = new Uint8Array(KEY_BYTES);
  for (let i = 0; i < KEY_BYTES; i++) {
    raw[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return raw;
}

async function deriveKek(
  masterCode: string,
  salt: Uint8Array,
  opts: { timeCost: number; memoryCost: number; parallelism: number },
): Promise<CryptoKey> {
  const raw = await deriveKekRaw(masterCode, salt, opts);
  return crypto.subtle.importKey('raw', bs(raw), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

// =====================================================================
// Per-user keypair: generate, wrap with Master Code, unwrap on sign-in
// =====================================================================

export interface KeypairBundle {
  publicKey: string; // base64 X25519 public key
  encryptedPrivateKey: EncryptedPrivateKey;
  privateKey: Uint8Array; // raw (32 bytes) - held in memory only
}

/** Generate a fresh X25519 keypair, wrap the private key with a KEK derived from the Master Code. */
export async function generateKeypair(masterCode: string): Promise<KeypairBundle> {
  const priv = x25519.utils.randomSecretKey();
  const pub = x25519.getPublicKey(priv);

  const salt = randomBytes(16);
  const kdf = { timeCost: 3, memoryCost: 64 * 1024, parallelism: 1 };
  const kek = await deriveKek(masterCode, salt, kdf);
  const iv = randomBytes(GCM_IV_BYTES);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, kek, bs(priv)),
  );

  return {
    publicKey: toB64(pub),
    encryptedPrivateKey: {
      kdf: {
        algorithm: 'argon2id',
        salt: toB64(salt),
        timeCost: kdf.timeCost,
        memoryCost: kdf.memoryCost,
        parallelism: kdf.parallelism,
      },
      cipher: {
        algorithm: 'aes-256-gcm',
        iv: toB64(iv),
        ciphertext: toB64(ciphertext),
      },
      version: 1,
    },
    privateKey: priv,
  };
}

/** Decrypt the X25519 private key using the Master Code. */
export async function unlockPrivateKey(
  encrypted: EncryptedPrivateKey,
  masterCode: string,
): Promise<Uint8Array> {
  const salt = fromB64(encrypted.kdf.salt);
  const kek = await deriveKek(masterCode, salt, {
    timeCost: encrypted.kdf.timeCost,
    memoryCost: encrypted.kdf.memoryCost,
    parallelism: encrypted.kdf.parallelism,
  });
  const iv = fromB64(encrypted.cipher.iv);
  const ct = fromB64(encrypted.cipher.ciphertext);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bs(iv) }, kek, bs(ct));
    return new Uint8Array(plain);
  } catch {
    throw new Error('Incorrect Master Code.');
  }
}

// =====================================================================
// Shared vault key: generate, seal to a public key, unseal with private key
// =====================================================================

export function generateVaultKey(): Uint8Array {
  return randomBytes(KEY_BYTES);
}

/** Seal the 32-byte vault key to a recipient's X25519 public key. */
export async function sealToPublicKey(
  vaultKey: Uint8Array,
  recipientPublicKey: string,
): Promise<WrappedVaultKey> {
  const recipientPub = fromB64(recipientPublicKey);
  const ephemeralPriv = x25519.utils.randomSecretKey();
  const ephemeralPub = x25519.getPublicKey(ephemeralPriv);
  const shared = x25519.getSharedSecret(ephemeralPriv, recipientPub);
  const kMaterial = hkdf(sha256, shared, undefined, WRAP_INFO, KEY_BYTES);
  const k = await crypto.subtle.importKey('raw', bs(kMaterial), { name: 'AES-GCM' }, false, [
    'encrypt',
  ]);
  const iv = randomBytes(GCM_IV_BYTES);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, k, bs(vaultKey)),
  );
  return {
    ephemeralPublicKey: toB64(ephemeralPub),
    iv: toB64(iv),
    ciphertext: toB64(ciphertext),
    version: 1,
  };
}

/** Unseal a wrapped vault key using the current user's X25519 private key. */
export async function openWrappedKey(
  wrapped: WrappedVaultKey,
  privateKey: Uint8Array,
): Promise<Uint8Array> {
  const ephemeralPub = fromB64(wrapped.ephemeralPublicKey);
  const shared = x25519.getSharedSecret(privateKey, ephemeralPub);
  const kMaterial = hkdf(sha256, shared, undefined, WRAP_INFO, KEY_BYTES);
  const k = await crypto.subtle.importKey('raw', bs(kMaterial), { name: 'AES-GCM' }, false, [
    'decrypt',
  ]);
  const iv = fromB64(wrapped.iv);
  const ct = fromB64(wrapped.ciphertext);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bs(iv) }, k, bs(ct));
    return new Uint8Array(plain);
  } catch {
    throw new Error('Unable to unwrap vault key.');
  }
}

/** Import a raw 32-byte vault key as a non-extractable CryptoKey for data encryption. */
export async function importVaultKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', bs(raw), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

// =====================================================================
// KEK-wrapped keypair: X25519 keypair whose private key is AES-GCM-wrapped
// with a random 32-byte KEK (no Argon2id — there's no password to derive
// from). The KEK is displayed to the operator once and pasted into an env
// var on the VPS; the consuming service decrypts the private key at boot
// and drops the KEK. Used by the scheduled-backup enrolment flow.
// =====================================================================

export interface KekWrappedKeypairBundle {
  publicKey: string; // base64 X25519 public key
  encryptedPrivateKey: {
    cipher: { algorithm: 'aes-256-gcm'; iv: string; ciphertext: string };
    version: 1;
  };
  privateKey: Uint8Array; // raw — held in memory only during enrolment
  kek: Uint8Array; // raw 32 bytes — shown to admin once, then zeroed
}

export async function generateKekWrappedKeypair(): Promise<KekWrappedKeypairBundle> {
  const priv = x25519.utils.randomSecretKey();
  const pub = x25519.getPublicKey(priv);
  const kek = randomBytes(KEY_BYTES);
  const kekKey = await crypto.subtle.importKey('raw', bs(kek), { name: 'AES-GCM' }, false, [
    'encrypt',
  ]);
  const iv = randomBytes(GCM_IV_BYTES);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, kekKey, bs(priv)),
  );
  return {
    publicKey: toB64(pub),
    encryptedPrivateKey: {
      cipher: {
        algorithm: 'aes-256-gcm',
        iv: toB64(iv),
        ciphertext: toB64(ciphertext),
      },
      version: 1,
    },
    privateKey: priv,
    kek,
  };
}

// =====================================================================
// Payload encryption (unchanged)
// =====================================================================

export async function encryptJson(data: unknown, key: CryptoKey): Promise<EncryptedPayload> {
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const iv = randomBytes(GCM_IV_BYTES);
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, key, bs(plaintext)),
  );
  return { iv: toB64(iv), ciphertext: toB64(cipher), version: 0 };
}

export async function decryptJson<T = unknown>(
  payload: EncryptedPayload,
  key: CryptoKey,
): Promise<T> {
  const iv = fromB64(payload.iv);
  const cipher = fromB64(payload.ciphertext);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bs(iv) }, key, bs(cipher));
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

export async function encryptBlob(data: Uint8Array, key: CryptoKey) {
  const iv = randomBytes(GCM_IV_BYTES);
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, key, bs(data)),
  );
  return { iv, ciphertext: cipher };
}

export async function decryptBlob(iv: Uint8Array, ciphertext: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bs(iv) }, key, bs(ciphertext)),
  );
}

export { toB64, fromB64 };
